import React, { useState, useEffect, useMemo, useRef } from 'react'
import { AnimatePresence } from 'framer-motion'
import { useParams, useNavigate } from 'react-router-dom'
import { useQuery, useQueryClient } from '@tanstack/react-query'
import { supabase } from '@/lib/supabase'
import { useAuth } from '@/lib/auth'
import { formatCurrency, formatDate } from '@/lib/utils'
import { contactSnapshot } from '@/lib/contactSnapshot'
import { ArrowLeft, Repeat, Pause, Play, CheckCircle2, Trash2, UserPlus } from 'lucide-react'
import { toast } from 'sonner'
import LineItemEditor, { type LineItem } from '@/components/payments/LineItemEditor'
import LinkContactModal from '@/components/payments/LinkContactModal'
import TotalsPanel from '@/components/payments/TotalsPanel'
import { computeTotals, lineTotal, type DiscountType } from '@/lib/totals'
import type { Frequency } from '@/lib/recurringInvoiceCadence'
import CadencePicker, { type EndCondition } from '@/components/payments/CadencePicker'

// Used for header copy ("Recurring · Monthly · …") and the
// frequency-change disclosure ("…the new Quarterly cadence applies").
// Frequency labels in the cadence select itself come from CadencePicker.
const FREQUENCY_LABELS: Record<string, string> = {
  weekly: 'Weekly', monthly: 'Monthly', quarterly: 'Quarterly', yearly: 'Yearly',
}

const STATUS_CONFIG: Record<string, { label: string; color: string; bg: string }> = {
  active:   { label: 'Active',   color: 'var(--positive)',     bg: 'var(--positive-dim)' },
  paused:   { label: 'Paused',   color: 'var(--warning)',      bg: 'var(--warning-dim)' },
  finished: { label: 'Finished', color: 'var(--ink-tertiary)', bg: 'var(--border-subtle)' },
}

// payment_terms is stored as a string token on the template + parsed by the
// derive_invoice_due_date trigger on insert (parse_payment_terms_days). The
// editor exposes a fixed set; if a series has a value outside this list,
// the option is added inline so we never lose the operator's prior choice.
const PAYMENT_TERMS_OPTIONS: { value: string; label: string }[] = [
  { value: 'on_receipt', label: 'Due on receipt' },
  { value: 'net_7',      label: 'Net 7' },
  { value: 'net_15',     label: 'Net 15' },
  { value: 'net_30',     label: 'Net 30' },
  { value: 'net_45',     label: 'Net 45' },
  { value: 'net_60',     label: 'Net 60' },
]

// Curated ISO 4217 list for the currency picker. currency has no DB column or
// CHECK (it lives in template.currency), but formatCurrency/Intl.NumberFormat
// needs a valid code — so a curated select, not a freeform field. A series
// whose stored value falls outside this list gets it prepended on render (see
// currencyOptions below) so opening the page never silently changes it.
const CURRENCY_OPTIONS = ['USD', 'CAD', 'EUR', 'GBP', 'AUD', 'NZD', 'JPY', 'CHF', 'SGD', 'HKD']

interface SeriesRow {
  id: string
  workspace_id: string
  contact_id: string
  company_id: string | null
  service_line_id: string | null
  title: string
  template: any
  frequency: Frequency
  start_date: string
  end_date: string | null
  end_after_count: number | null
  generated_count: number
  next_run_date: string
  send_mode: 'draft' | 'auto_send'
  status: 'active' | 'paused' | 'finished'
  created_at: string
  updated_at: string
}

interface GeneratedInvoiceRow {
  id: string
  invoice_number: string
  status: string
  total: number | null
  currency: string | null
  issue_date: string
  due_date: string | null
}

export default function RecurringInvoiceDetail() {
  const { id } = useParams()
  const navigate = useNavigate()
  const { workspace } = useAuth()
  const queryClient = useQueryClient()

  // ─── Form state — hydrated from series in the effect below ──────────────
  const [title, setTitle] = useState('')
  const [frequency, setFrequency] = useState<Frequency>('monthly')
  const [startDate, setStartDate] = useState('')
  const [endCondition, setEndCondition] = useState<EndCondition>('never')
  const [endDate, setEndDate] = useState('')
  const [endAfterCount, setEndAfterCount] = useState('')
  const [sendMode, setSendMode] = useState<'draft' | 'auto_send'>('draft')
  const [templateLineItems, setTemplateLineItems] = useState<LineItem[]>([])
  const [discountType, setDiscountType] = useState<DiscountType>('none')
  const [discountValue, setDiscountValue] = useState(0)
  const [taxRate, setTaxRate] = useState(0)
  const [paymentTerms, setPaymentTerms] = useState('net_30')
  const [notes, setNotes] = useState('')
  // Billing presentation CP5: series-level amount-only flag; generated children
  // inherit it via template.display_amount_only.
  const [displayAmountOnly, setDisplayAmountOnly] = useState(false)
  // Customer — staged into form state by the re-pick, persisted by handleSave.
  // contactId mirrors the series.contact_id COLUMN; the other three mirror the
  // template JSONB snapshot keys (contact_name/contact_email/company_name).
  const [contactId, setContactId] = useState('')
  const [contactName, setContactName] = useState('')
  const [contactEmail, setContactEmail] = useState('')
  const [companyName, setCompanyName] = useState('')
  // Currency — staged into form state; persisted by handleSave into
  // template.currency. Lives only in the template JSONB (no column).
  const [currency, setCurrency] = useState('USD')
  const [showChangeContact, setShowChangeContact] = useState(false)
  const [saving, setSaving] = useState(false)
  // Dirty tracking: snapshot of the form at last hydrate (or last save) so
  // Save can toggle primary/ghost based on actual diff vs. persisted state.
  const pristineRef = useRef<string>('')

  // ─── Queries ────────────────────────────────────────────────────────────
  const { data: series, isLoading, error: seriesError } = useQuery<SeriesRow>({
    queryKey: ['recurring-series-detail', id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('recurring_invoice_series')
        .select('*')
        .eq('id', id!)
        .single()
      if (error) throw error
      return data as SeriesRow
    },
    enabled: !!id,
  })

  const { data: genInvoices = [] } = useQuery<GeneratedInvoiceRow[]>({
    queryKey: ['recurring-series-invoices', id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('invoices')
        .select('id, invoice_number, status, total, currency, issue_date, due_date')
        .eq('recurring_invoice_series_id', id!)
        .order('created_at', { ascending: false })
      if (error) throw error
      return (data ?? []) as GeneratedInvoiceRow[]
    },
    enabled: !!id,
  })

  // ─── Hydrate form from series ───────────────────────────────────────────
  useEffect(() => {
    if (!series) return
    const tpl = (series.template ?? {}) as Record<string, any>
    setTitle(series.title)
    setFrequency(series.frequency)
    setStartDate(series.start_date)
    if (series.end_date)         { setEndCondition('on_date');     setEndDate(series.end_date) }
    else if (series.end_after_count != null) { setEndCondition('after_count'); setEndAfterCount(String(series.end_after_count)) }
    else                         { setEndCondition('never') }
    setSendMode(series.send_mode)
    setTemplateLineItems((tpl.line_items ?? []).map((li: any, i: number) => ({
      id: li.id ?? String(Date.now() + i),
      product_id: li.product_id,
      name: li.name ?? '',
      description: li.description ?? '',
      qty: Number(li.qty) || 0,
      unit: li.unit ?? 'unit',
      unit_price: Number(li.unit_price) || 0,
      total: (Number(li.qty) || 0) * (Number(li.unit_price) || 0),
    })))
    setDiscountType((tpl.discount_type as DiscountType) ?? 'none')
    setDiscountValue(Number(tpl.discount_value) || 0)
    setTaxRate(Number(tpl.tax_rate) || 0)
    setPaymentTerms(tpl.payment_terms ?? 'net_30')
    setNotes(tpl.notes ?? '')
    setDisplayAmountOnly(!!tpl.display_amount_only)
    setContactId(series.contact_id)
    setContactName(tpl.contact_name ?? '')
    setContactEmail(tpl.contact_email ?? '')
    setCompanyName(tpl.company_name ?? '')
    setCurrency(tpl.currency ?? 'USD')

    // Snapshot pristine state AFTER hydration completes. Defer to next tick
    // so all setState calls flush before we serialize.
    queueMicrotask(() => {
      pristineRef.current = serializeForm({
        title: series.title,
        frequency: series.frequency,
        startDate: series.start_date,
        endCondition: series.end_date ? 'on_date' : series.end_after_count != null ? 'after_count' : 'never',
        endDate: series.end_date ?? '',
        endAfterCount: series.end_after_count != null ? String(series.end_after_count) : '',
        sendMode: series.send_mode,
        templateLineItems: tpl.line_items ?? [],
        discountType: tpl.discount_type ?? 'none',
        discountValue: Number(tpl.discount_value) || 0,
        taxRate: Number(tpl.tax_rate) || 0,
        paymentTerms: tpl.payment_terms ?? 'net_30',
        notes: tpl.notes ?? '',
        displayAmountOnly: !!tpl.display_amount_only,
        contactId: series.contact_id,
        contactName: tpl.contact_name ?? '',
        contactEmail: tpl.contact_email ?? '',
        companyName: tpl.company_name ?? '',
        currency: tpl.currency ?? 'USD',
      })
    })
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [series?.id])

  // ─── Dirty tracking ─────────────────────────────────────────────────────
  const formSnapshot = useMemo(() => serializeForm({
    title, frequency, startDate, endCondition, endDate, endAfterCount, sendMode,
    templateLineItems, discountType, discountValue, taxRate, paymentTerms, notes,
    contactId, contactName, contactEmail, companyName, currency, displayAmountOnly,
  }), [
    title, frequency, startDate, endCondition, endDate, endAfterCount, sendMode,
    templateLineItems, discountType, discountValue, taxRate, paymentTerms, notes,
    contactId, contactName, contactEmail, companyName, currency, displayAmountOnly,
  ])
  const dirty = pristineRef.current !== '' && formSnapshot !== pristineRef.current

  // ─── Live totals + cadence preview ──────────────────────────────────────
  const { subtotal, discountAmount, taxAmount, total } = useMemo(() => computeTotals({
    lineItems: templateLineItems,
    discountType,
    discountValue,
    taxRate,
    // Arc B Slice 1: the recurring series template has no minimum_charge input
    // yet (CP2) — no floor for now.
    minimumCharge: null,
  }), [templateLineItems, discountType, discountValue, taxRate])

  // ─── Handlers ───────────────────────────────────────────────────────────
  const handleSave = async () => {
    if (!series) return
    if (!title.trim()) { toast.error('Schedule needs a title'); return }
    if (!startDate)    { toast.error('Pick a start date'); return }
    if (endCondition === 'on_date' && !endDate)   { toast.error('Pick an end date'); return }
    if (endCondition === 'after_count') {
      const n = parseInt(endAfterCount, 10)
      if (!Number.isFinite(n) || n < 1) { toast.error('After count must be at least 1'); return }
    }
    // contact_id is NOT NULL on the series and must be a real contact — by
    // construction the re-pick only stages real contacts, but guard anyway.
    if (!contactId) { toast.error('Pick a customer'); return }

    // Rebuild the template JSONB. Customer snapshot fields + currency come
    // from staged form state; footer_text stays pass-through; service_line_id
    // mirrors the column.
    const tpl = (series.template ?? {}) as Record<string, any>
    const template: Record<string, any> = {
      line_items: templateLineItems.map(li => ({
        id: li.id,
        product_id: li.product_id,
        name: li.name,
        description: li.description,
        qty: li.qty,
        unit: li.unit,
        unit_price: li.unit_price,
        total: lineTotal(li), // Slice 2: flat for now (recurring tier UI deferred)
      })),
      contact_name:  contactName,
      contact_email: contactEmail || '',
      company_name:  companyName || null,
      payment_terms: paymentTerms,
      discount_type: discountType,
      discount_value: discountValue,
      tax_rate: taxRate,
      notes: notes || null,
      display_amount_only: displayAmountOnly,
      footer_text: tpl.footer_text ?? null,
      service_line_id: series.service_line_id,
      currency: currency,
    }

    // ─── next_run_date recompute rule ────────────────────────────────────
    // ONLY when start_date changed AND the series has never fired yet.
    // Editing frequency does NOT shift the next scheduled run — the run
    // already committed honors itself; the new cadence applies on the
    // sweep's next advance. Editing start_date on a series that has
    // already generated invoices is informational only.
    const startDateChanged = startDate !== series.start_date
    const recomputeNextRun = startDateChanged && series.generated_count === 0

    const updates: Record<string, any> = {
      title: title.trim(),
      frequency,
      start_date: startDate,
      end_date: endCondition === 'on_date' ? endDate : null,
      end_after_count: endCondition === 'after_count' ? parseInt(endAfterCount, 10) : null,
      send_mode: sendMode,
      // Customer COLUMNS — contact_id is the staged real contact; company_id is
      // null to mirror NewRecurringScheduleModal (contacts don't expose a
      // company FK; company is denormalized into the template's company_name).
      contact_id: contactId,
      company_id: null,
      template,
    }
    if (recomputeNextRun) updates.next_run_date = startDate

    setSaving(true)
    const { error } = await supabase
      .from('recurring_invoice_series')
      .update(updates)
      .eq('id', series.id)
    setSaving(false)
    if (error) { toast.error(`Save failed: ${error.message}`); return }
    toast.success('Schedule saved')
    pristineRef.current = formSnapshot
    queryClient.invalidateQueries({ queryKey: ['recurring-series-detail', id] })
    queryClient.invalidateQueries({ queryKey: ['recurring-invoice-series', workspace?.id] })
  }

  const updateStatus = async (next: 'active' | 'paused' | 'finished') => {
    if (!series) return
    const { error } = await supabase
      .from('recurring_invoice_series')
      .update({ status: next })
      .eq('id', series.id)
    if (error) { toast.error(`Status update failed: ${error.message}`); return }
    const verb = next === 'paused' ? 'paused' : next === 'active' ? 'resumed' : 'marked finished'
    toast.success(`Schedule ${verb}`)
    queryClient.invalidateQueries({ queryKey: ['recurring-series-detail', id] })
    queryClient.invalidateQueries({ queryKey: ['recurring-invoice-series', workspace?.id] })
  }

  const handleMarkFinished = async () => {
    // eslint-disable-next-line no-alert
    if (!confirm("Mark this schedule as finished? It won't generate any more invoices. You can't undo this.")) return
    await updateStatus('finished')
  }

  const handleDelete = async () => {
    if (!series) return
    const count = series.generated_count || 0
    const tail = count === 0
      ? 'No invoices have been generated yet.'
      : `The ${count} invoice${count === 1 ? '' : 's'} it generated will be kept.`
    // eslint-disable-next-line no-alert
    if (!confirm(`Delete this schedule? ${tail}`)) return
    const { error } = await supabase
      .from('recurring_invoice_series')
      .delete()
      .eq('id', series.id)
    if (error) { toast.error(`Delete failed: ${error.message}`); return }
    toast.success('Schedule deleted')
    queryClient.invalidateQueries({ queryKey: ['recurring-invoice-series', workspace?.id] })
    navigate('/app/invoices/recurring')
  }

  // ─── Render guards ──────────────────────────────────────────────────────
  if (isLoading) return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', background: 'var(--canvas)' }}>
      <div style={{ width: 32, height: 32, borderRadius: '50%', border: '2px solid var(--accent)', borderTopColor: 'transparent', animation: 'spin 0.8s linear infinite' }} />
    </div>
  )
  if (seriesError) return <div style={{ padding: 40, textAlign: 'center', color: 'var(--danger)' }}>Failed to load: {(seriesError as Error).message}</div>
  if (!series) return <div style={{ padding: 40, textAlign: 'center', color: 'var(--ink-secondary)' }}>Schedule not found</div>

  const cfg = STATUS_CONFIG[series.status] || STATUS_CONFIG.active
  // Customer + currency display read STAGED form state so an edit is reflected
  // before Save. (Both are hydrated from the series/template in the effect.)
  const isFinished   = series.status === 'finished'

  // Form control styles (local — series-detail inputs use the same surface
  // chrome as the modal inputs from MakeRecurringModal).
  const inp: React.CSSProperties = { width: '100%', padding: '9px 12px', background: 'var(--field-bg)', border: '1px solid var(--border-default)', borderRadius: 9, fontSize: 13, color: 'var(--ink-primary)', outline: 'none', boxSizing: 'border-box' }
  const lbl: React.CSSProperties = { fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', display: 'block', marginBottom: 6, textTransform: 'uppercase', letterSpacing: '0.06em' }
  const sectionH: React.CSSProperties = { fontSize: 13, fontWeight: 700, color: 'var(--ink-primary)', textTransform: 'uppercase', letterSpacing: '0.08em', margin: '0 0 14px' }

  // If the series has a payment_terms value outside our fixed option list,
  // synthesize an extra option so the operator never sees their stored
  // choice replaced silently on save.
  const paymentTermsOptions = PAYMENT_TERMS_OPTIONS.some(o => o.value === paymentTerms)
    ? PAYMENT_TERMS_OPTIONS
    : [{ value: paymentTerms, label: paymentTerms }, ...PAYMENT_TERMS_OPTIONS]

  // If the series' stored currency is outside the curated list (legacy value),
  // prepend it so the select shows it and opening the page never silently
  // changes it on the next save.
  const currencyOptions = CURRENCY_OPTIONS.includes(currency)
    ? CURRENCY_OPTIONS
    : [currency, ...CURRENCY_OPTIONS]

  return (
    <div style={{ height: '100%', display: 'flex', flexDirection: 'column', background: 'var(--canvas)' }}>

      {/* Top bar */}
      <div style={{ padding: '14px 24px', borderBottom: '1px solid var(--border-subtle)', display: 'flex', alignItems: 'center', gap: 12, flexShrink: 0, background: 'var(--surface-raised)' }}>
        <button onClick={() => { if (window.history.length > 1) navigate(-1); else navigate('/app/invoices/recurring') }}
          style={{ display: 'flex', alignItems: 'center', gap: 5, fontSize: 13, color: 'var(--ink-tertiary)', background: 'none', border: 'none', cursor: 'pointer', padding: 0 }}
          onMouseEnter={e => e.currentTarget.style.color = 'var(--ink-primary)'}
          onMouseLeave={e => e.currentTarget.style.color = 'var(--ink-tertiary)'}>
          <ArrowLeft style={{ width: 14, height: 14 }} /> Back
        </button>
        <div style={{ width: 1, height: 16, background: 'var(--border-default)' }} />
        <Repeat style={{ width: 14, height: 14, color: 'var(--accent)' }} />
        <span style={{ fontSize: 14, fontWeight: 700, color: 'var(--ink-primary)', maxWidth: 300, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' }}>{series.title}</span>
        <span style={{ fontSize: 11, fontWeight: 700, padding: '3px 9px', borderRadius: 100, background: cfg.bg, color: cfg.color, textTransform: 'capitalize' }}>
          {cfg.label}
        </span>
        <div style={{ marginLeft: 'auto', display: 'flex', gap: 8 }}>
          {!isFinished && (
            <button onClick={handleSave} disabled={saving || !dirty}
              style={dirty
                ? { display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px', background: 'var(--accent)', border: '1px solid var(--accent)', borderRadius: 8, color: 'var(--accent-ink)', fontSize: 12, fontWeight: 700, cursor: 'pointer' }
                : { padding: '7px 14px', background: 'var(--hover-bg)', border: '1px solid var(--border-default)', borderRadius: 8, color: 'var(--ink-secondary)', fontSize: 12, fontWeight: 600, cursor: 'default', opacity: 0.7 }}>
              {saving ? 'Saving...' : 'Save'}
            </button>
          )}
          {series.status === 'active' && (
            <button onClick={() => updateStatus('paused')}
              style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px', background: 'var(--hover-bg)', border: '1px solid var(--border-default)', borderRadius: 8, color: 'var(--ink-secondary)', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
              <Pause style={{ width: 13, height: 13 }} /> Pause
            </button>
          )}
          {series.status === 'paused' && (
            <button onClick={() => updateStatus('active')}
              style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px', background: 'var(--hover-bg)', border: '1px solid var(--border-default)', borderRadius: 8, color: 'var(--ink-secondary)', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
              <Play style={{ width: 13, height: 13 }} /> Resume
            </button>
          )}
          {!isFinished && (
            <button onClick={handleMarkFinished}
              style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px', background: 'color-mix(in srgb, var(--positive) var(--status-tint-dim), transparent)', border: '1px solid var(--positive-border)', borderRadius: 8, color: 'var(--positive)', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
              <CheckCircle2 style={{ width: 13, height: 13 }} /> Mark finished
            </button>
          )}
          <button onClick={handleDelete}
            style={{ display: 'flex', alignItems: 'center', gap: 6, padding: '7px 14px', background: 'color-mix(in srgb, var(--danger) var(--status-tint-faint), transparent)', border: '1px solid var(--danger-border)', borderRadius: 8, color: 'var(--danger)', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
            <Trash2 style={{ width: 13, height: 13 }} /> Delete
          </button>
        </div>
      </div>

      {/* Body */}
      <div style={{ flex: 1, overflow: 'auto', padding: '28px 32px' }}>
        <div style={{ maxWidth: 800, margin: '0 auto' }}>

          {/* Series header */}
          <div style={{ marginBottom: 28 }}>
            <h1 style={{ fontSize: 24, fontWeight: 900, color: 'var(--ink-primary)', margin: '0 0 6px', letterSpacing: '-0.02em' }}>{series.title}</h1>
            <p style={{ fontSize: 13, color: 'var(--ink-tertiary)', margin: 0 }}>
              Recurring · {FREQUENCY_LABELS[series.frequency] || series.frequency} · started {formatDate(series.start_date, 'MMM d, yyyy')}
              {' · '}
              {isFinished ? 'Finished' : <>next run <strong style={{ color: 'var(--ink-secondary)', fontWeight: 600 }}>{formatDate(series.next_run_date, 'MMM d, yyyy')}</strong></>}
              {' · '}
              <strong style={{ color: 'var(--ink-secondary)', fontWeight: 600 }}>{series.generated_count}</strong> generated
            </p>
          </div>

          {/* Bill to — customer is editable. "Change customer" stages the pick
              into form state; the existing Save persists it (columns +
              template). Past generated invoices are snapshots and unchanged. */}
          <div style={{ marginBottom: 32, padding: '16px 20px', background: 'var(--surface)', border: '1px solid var(--border-subtle)', borderRadius: 12 }}>
            <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'space-between', gap: 12, marginBottom: 8 }}>
              <p style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', textTransform: 'uppercase', letterSpacing: '0.08em', margin: 0 }}>Bill to</p>
              {!isFinished && (
                <button onClick={() => setShowChangeContact(true)}
                  style={{ display: 'inline-flex', alignItems: 'center', gap: 5, padding: '4px 10px', background: 'var(--accent-dim)', border: '1px solid var(--accent-border)', borderRadius: 8, color: 'var(--accent-text)', fontSize: 12, fontWeight: 600, cursor: 'pointer' }}>
                  <UserPlus style={{ width: 13, height: 13 }} /> Change customer
                </button>
              )}
            </div>
            <p style={{ fontSize: 15, fontWeight: 700, color: 'var(--ink-primary)', margin: '0 0 4px' }}>{contactName || '—'}</p>
            {companyName  && <p style={{ fontSize: 13, color: 'var(--ink-secondary)', margin: '0 0 4px' }}>{companyName}</p>}
            {contactEmail && <p style={{ fontSize: 13, color: 'var(--ink-tertiary)', margin: 0 }}>{contactEmail}</p>}
          </div>

          {/* ─── CADENCE section ──────────────────────────────────────── */}
          <div style={{ marginBottom: 32 }}>
            <h2 style={sectionH}>Cadence</h2>

            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
              <div>
                <label style={lbl}>Schedule title</label>
                <input value={title} onChange={e => setTitle(e.target.value)}
                  style={inp}
                  onFocus={e => e.currentTarget.style.borderColor='var(--focus-ring)'}
                  onBlur={e => e.currentTarget.style.borderColor='var(--border-default)'} />
              </div>

              <CadencePicker
                frequency={frequency}
                startDate={startDate}
                endCondition={endCondition}
                endDate={endDate}
                endAfterCount={endAfterCount}
                sendMode={sendMode}
                onFrequencyChange={setFrequency}
                onStartDateChange={setStartDate}
                onEndConditionChange={setEndCondition}
                onEndDateChange={setEndDate}
                onEndAfterCountChange={setEndAfterCount}
                onSendModeChange={setSendMode}
                previewHeader="next"
                inlineNotice={
                  series.generated_count > 0 && frequency !== series.frequency
                    ? (
                      <div style={{ padding: '10px 14px', background: 'var(--warning-dim)', border: '1px solid var(--warning-border)', borderRadius: 9, fontSize: 12, color: 'var(--ink-secondary)' }}>
                        The next run on <strong style={{ color: 'var(--ink-primary)' }}>{formatDate(series.next_run_date, 'MMM d, yyyy')}</strong> stays as scheduled. After that, the new {FREQUENCY_LABELS[frequency] || frequency} cadence applies.
                      </div>
                    )
                    : null
                }
              />
            </div>
          </div>

          {/* ─── TEMPLATE section ─────────────────────────────────────── */}
          <div style={{ marginBottom: 32 }}>
            <h2 style={sectionH}>Template</h2>
            <p style={{ fontSize: 12, color: 'var(--ink-tertiary)', margin: '0 0 16px', lineHeight: 1.6 }}>
              Changes apply to invoices generated from this point forward — past invoices are unchanged.
            </p>

            <LineItemEditor
              items={templateLineItems}
              onItemsChange={setTemplateLineItems}
              isLocked={false}
              workspaceId={workspace!.id}
            />

            {/* Totals (inline-copy from InvoiceDetail; series has no
                amount_paid/amount_due — those are per-invoice concepts) */}
            <div style={{ display: 'flex', justifyContent: 'flex-end', marginBottom: 20 }}>
              <TotalsPanel
                subtotal={subtotal}
                discountType={discountType}
                discountValue={discountValue}
                discountAmount={discountAmount}
                onDiscountTypeChange={setDiscountType}
                onDiscountValueChange={setDiscountValue}
                taxRate={taxRate}
                taxAmount={taxAmount}
                onTaxRateChange={setTaxRate}
                total={total}
                currency={currency}
                isLocked={false}
              />
            </div>

            {/* Billing presentation CP5: amount-only display for this series.
                Persists to template.display_amount_only; generated children inherit it. */}
            <div style={{ marginBottom: 16, display: 'flex', alignItems: 'flex-start', justifyContent: 'space-between', gap: 16 }}>
              <div style={{ flex: 1, minWidth: 0 }}>
                <p style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink-primary)', margin: '0 0 3px' }}>Amount-only display</p>
                <p style={{ fontSize: 12, color: 'var(--ink-secondary)', margin: 0, lineHeight: 1.5 }}>
                  Hide quantities and rates on the customer's invoice. Totals are unchanged. Applies to future invoices generated from this series.
                </p>
              </div>
              <div role="switch" aria-checked={displayAmountOnly} tabIndex={0}
                onClick={() => setDisplayAmountOnly(v => !v)}
                onKeyDown={e => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); setDisplayAmountOnly(v => !v) } }}
                style={{ width: 36, height: 20, borderRadius: 100, background: displayAmountOnly ? 'var(--accent)' : 'var(--border-default)', transition: 'background 200ms ease', position: 'relative', flexShrink: 0, cursor: 'pointer', marginTop: 2 }}>
                <div style={{ position: 'absolute', top: 3, left: displayAmountOnly ? 19 : 3, width: 14, height: 14, borderRadius: '50%', background: 'var(--accent-ink)', transition: 'left 200ms ease', boxShadow: 'var(--shadow-knob)' }} />
              </div>
            </div>

            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 16 }}>
              <div>
                <label style={lbl}>Payment terms</label>
                <select value={paymentTerms} onChange={e => setPaymentTerms(e.target.value)} style={{ ...inp, cursor: 'pointer' }}>
                  {paymentTermsOptions.map(o => <option key={o.value} value={o.value}>{o.label}</option>)}
                </select>
              </div>
              <div>
                <label style={lbl}>Currency</label>
                <select value={currency} onChange={e => setCurrency(e.target.value)} disabled={isFinished}
                  style={{ ...inp, cursor: isFinished ? 'not-allowed' : 'pointer', opacity: isFinished ? 0.7 : 1 }}>
                  {currencyOptions.map(c => <option key={c} value={c}>{c}</option>)}
                </select>
              </div>
            </div>

            <div>
              <label style={lbl}>Notes (printed on the invoice)</label>
              <textarea value={notes} onChange={e => setNotes(e.target.value)} rows={3}
                placeholder="Thank you note, payment instructions, …"
                style={{ ...inp, resize: 'vertical', lineHeight: 1.5 }}
                onFocus={e => e.currentTarget.style.borderColor='var(--focus-ring)'}
                onBlur={e => e.currentTarget.style.borderColor='var(--border-default)'} />
            </div>
          </div>

          {/* ─── GENERATED INVOICES section ───────────────────────────── */}
          <div style={{ marginBottom: 40 }}>
            <h2 style={sectionH}>Generated invoices ({genInvoices.length})</h2>

            <div style={{ background: 'var(--surface)', border: '1px solid var(--border-subtle)', borderRadius: 12, overflow: 'hidden' }}>
              {genInvoices.length === 0 ? (
                <div style={{ padding: '32px 24px', textAlign: 'center' }}>
                  <p style={{ fontSize: 13, color: 'var(--ink-secondary)', margin: '0 0 4px' }}>No invoices generated yet.</p>
                  <p style={{ fontSize: 12, color: 'var(--ink-tertiary)', margin: 0 }}>
                    {series.status === 'paused' ? 'Resume the schedule to start generating.'
                      : isFinished ? 'This schedule is finished.'
                      : <>The first will land on <strong>{formatDate(series.next_run_date, 'MMM d, yyyy')}</strong>.</>}
                  </p>
                </div>
              ) : (
                <>
                  <div style={{ display: 'grid', gridTemplateColumns: '140px 1fr 120px 100px', padding: '10px 16px', borderBottom: '1px solid var(--border-subtle)' }}>
                    {['Invoice', 'Issued', 'Amount', 'Status'].map((h, i) => (
                      <div key={i} style={{ fontSize: 11, fontWeight: 600, color: 'var(--ink-tertiary)', textTransform: 'uppercase', letterSpacing: '0.08em' }}>{h}</div>
                    ))}
                  </div>
                  {genInvoices.map((inv, i) => {
                    const invCfg = INVOICE_STATUS_CONFIG[inv.status] || INVOICE_STATUS_CONFIG.draft
                    const isLast = i === genInvoices.length - 1
                    return (
                      <div key={inv.id}
                        onClick={() => navigate('/app/invoices/' + inv.id)}
                        style={{ display: 'grid', gridTemplateColumns: '140px 1fr 120px 100px', padding: '13px 16px', borderBottom: isLast ? 'none' : '1px solid var(--border-subtle)', cursor: 'pointer', transition: 'background 150ms ease', alignItems: 'center' }}
                        onMouseEnter={e => e.currentTarget.style.background = 'var(--hover-bg)'}
                        onMouseLeave={e => e.currentTarget.style.background = 'transparent'}>
                        <span style={{ fontSize: 13, fontWeight: 700, color: 'var(--accent)', fontFamily: 'monospace' }}>{inv.invoice_number}</span>
                        <span style={{ fontSize: 13, color: 'var(--ink-secondary)' }}>{formatDate(inv.issue_date, 'MMM d, yyyy')}</span>
                        <span style={{ fontSize: 13, fontWeight: 600, color: 'var(--ink-primary)' }}>{formatCurrency(Number(inv.total) || 0, inv.currency || 'USD')}</span>
                        <span style={{ fontSize: 11, fontWeight: 600, padding: '3px 9px', borderRadius: 100, background: invCfg.bg, color: invCfg.color, textTransform: 'capitalize', whiteSpace: 'nowrap', justifySelf: 'start' }}>
                          {invCfg.label}
                        </span>
                      </div>
                    )
                  })}
                </>
              )}
            </div>
          </div>

        </div>
      </div>

      <AnimatePresence>
        {showChangeContact && workspace && (
          <LinkContactModal
            workspaceId={workspace.id}
            onClose={() => setShowChangeContact(false)}
            onPick={(contact) => {
              // Stage the pick into form state — handleSave persists it (the
              // series columns + template). Does NOT write immediately.
              const snap = contactSnapshot(contact)
              setContactId(contact.id)
              setContactName(snap.contact_name)
              setContactEmail(contact.email || '')
              setCompanyName(snap.company_name ?? '')
            }}
          />
        )}
      </AnimatePresence>
    </div>
  )
}

// Mirror of STATUS_CONFIG from Invoices.tsx — for the generated-invoices
// list chips. Inlined to avoid a cross-file dependency for what's a small
// constant; if a third caller needs it, extract.
const INVOICE_STATUS_CONFIG: Record<string, { label: string; color: string; bg: string }> = {
  draft:   { label: 'Draft',   color: 'var(--ink-tertiary)', bg: 'var(--border-subtle)' },
  sent:    { label: 'Sent',    color: 'var(--info)',         bg: 'var(--info-dim)' },
  viewed:  { label: 'Viewed',  color: 'var(--accent-text)',  bg: 'var(--accent-dim)' },
  paid:    { label: 'Paid',    color: 'var(--positive)',     bg: 'var(--positive-dim)' },
  partial: { label: 'Partial', color: 'var(--warning)',      bg: 'var(--warning-dim)' },
  overdue: { label: 'Overdue', color: 'var(--danger)',       bg: 'var(--danger-dim)' },
  void:    { label: 'Void',    color: 'var(--ink-tertiary)', bg: 'var(--hover-bg)' },
}

// Stable serializer for dirty-tracking. line_items are reduced to the
// editable fields only (id/product_id is irrelevant for dirty intent — but
// safe to include; total is derived from qty*unit_price so we drop it).
function serializeForm(s: {
  title: string
  frequency: string
  startDate: string
  endCondition: string
  endDate: string
  endAfterCount: string
  sendMode: string
  templateLineItems: Array<{ name?: string; description?: string; qty?: number; unit?: string; unit_price?: number }>
  discountType: string
  discountValue: number
  taxRate: number
  paymentTerms: string
  notes: string
  contactId: string
  contactName: string
  contactEmail: string
  companyName: string
  currency: string
  displayAmountOnly: boolean
}): string {
  return JSON.stringify({
    title: s.title.trim(),
    frequency: s.frequency,
    startDate: s.startDate,
    endCondition: s.endCondition,
    endDate: s.endCondition === 'on_date' ? s.endDate : '',
    endAfterCount: s.endCondition === 'after_count' ? s.endAfterCount : '',
    sendMode: s.sendMode,
    templateLineItems: s.templateLineItems.map(li => ({
      name: li.name ?? '',
      description: li.description ?? '',
      qty: Number(li.qty) || 0,
      unit: li.unit ?? 'unit',
      unit_price: Number(li.unit_price) || 0,
    })),
    discountType: s.discountType,
    discountValue: s.discountValue,
    taxRate: s.taxRate,
    paymentTerms: s.paymentTerms,
    notes: s.notes,
    displayAmountOnly: s.displayAmountOnly,
    contactId: s.contactId,
    contactName: s.contactName,
    contactEmail: s.contactEmail,
    companyName: s.companyName,
    currency: s.currency,
  })
}
